import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  CreateClientAPI,
  GetClientsAPI,
  GetClientByIdAPI,
  UpdateClientAPI,
  DeleteClientAPI,
  UpdateAdminStatusAPI,
} from "../../api/clients";

export const createClient = createAsyncThunk(
  "clients/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await CreateClientAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create client");
    }
  }
);

export const getClients = createAsyncThunk(
  "clients/getAll",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await GetClientsAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch clients");
    }
  }
);

export const getClientById = createAsyncThunk(
  "clients/getById",
  async (clientId, { rejectWithValue }) => {
    try {
      const { data } = await GetClientByIdAPI(clientId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch client");
    }
  }
);

export const updateClient = createAsyncThunk(
  "clients/update",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await UpdateClientAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update client");
    }
  }
);

// Update admin status update
export const updateAdminStatus = createAsyncThunk(
  "users/updateAdminStatus",
  async ({ userId, action }, { rejectWithValue }) => {
    try {
      const response = await UpdateAdminStatusAPI({ userId, action });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteClient = createAsyncThunk(
  "clients/delete",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await DeleteClientAPI(clientId);
      return { apiRes: response.data, clientId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete client");
    }
  }
);

const clientSlice = createSlice({
  name: "clients",
  initialState: {
    list: [],
    selected: null,

    listLoading: false,
    listError: null,
    listSuccess: false,

    createLoading: false,
    createError: null,
    createSuccess: false,

    updateLoading: false,
    updateError: null,
    updateSuccess: false,

    statusUpdateLoading: false,
    statusUpdateError: null,
    statusUpdateSuccess: false,

    deleteLoading: false,
    deleteError: null,
    deleteSuccess: false,

    loading: false,
    error: null,
    success: false,

    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 1,
    },
  },
  reducers: {
    resetClientState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearSelectedClient: (state) => {
      state.selected = null;
    },
    resetCreateAdminState: (state) => {
      state.createLoading = false;
      state.createError = null;
      state.createSuccess = false;
    },
    reserDeleteAdminState: (state) => {
      state.deleteLoading = false;
      state.deleteError = null;
      state.deleteSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /* CREATE */
      .addCase(createClient.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.list.unshift(action.payload.data.user);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })

      /* GET ALL */
      .addCase(getClients.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listError = null;
        state.list = action.payload.data.users;

        state.pagination = action.payload.data.pagination;
      })
      .addCase(getClients.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
      })

      /* GET ONE */
      .addCase(getClientById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getClientById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.selected = action.payload.data;
      })
      .addCase(getClientById.rejected, (state, action) => {
        state.loading = false;
        state.selected = null;
        state.error = action.payload.message;
      })

      /* UPDATE */
      .addCase(updateClient.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })

      .addCase(updateClient.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        const updatedUser = action.payload.data.user;

        state.list = state.list.map((client) =>
          client._id === updatedUser._id ? updatedUser : client
        );
      })

      .addCase(updateClient.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.updateError = action.payload.message;
      })

      /*  STATUS UPDATE */
      .addCase(updateAdminStatus.pending, (state) => {
        state.statusUpdateLoading = true;
        state.statusUpdateError = null;
        state.statusUpdateSuccess = false;
      })
      .addCase(updateAdminStatus.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;
        state.statusUpdateSuccess = true;
        const updated = action.payload.data.user;

        if (!updated?._id) return;

        const idx = state.list.findIndex((u) => u._id === updated._id);
        if (idx !== -1) {
          state.list[idx] = {
            ...state.list[idx],
            status: updated.status === "activate" ? 1 : 0,
          };
        }
      })
      .addCase(updateAdminStatus.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.statusUpdateSuccess = false;
        state.statusUpdateError = action.payload.message;
      })
      /* DELETE */
      .addCase(deleteClient.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = null;

        const { clientId } = action.payload || {};
        if (!clientId) return;

        state.list = state.list.filter((u) => u._id !== clientId);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || "Failed to delete admin";
      });
  },
});

export const {
  resetClientState,
  clearSelectedClient,
  resetCreateAdminState,
  reserDeleteAdminState,
} = clientSlice.actions;

export default clientSlice.reducer;
